import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';

/**
 * Formats specifications object into a readable string
 * @param {Object} specs - The specifications object
 * @returns {String} Formatted specifications string
 */
const formatSpecifications = (specs) => {
  if (!specs || Object.keys(specs).length === 0) return '-';
  
  return Object.entries(specs)
    .map(([key, value]) => `${key}: ${value}`)
    .join(', ');
};

/**
 * Generates a summary from an array of assets
 * @param {Array} assets - Array of asset objects
 * @returns {Array} Array of {name, count} objects
 */
const generateSummary = (assets) => {
  if (!assets || assets.length === 0) return [];

  const summary = assets.reduce((acc, asset) => {
    const { name } = asset;
    if (!acc[name]) {
      acc[name] = 0;
    }
    acc[name]++;
    return acc;
  }, {});

  return Object.entries(summary).map(([name, count]) => ({ name, count }));
};

/**
 * Generates a PDF document containing asset information
 * @param {Object} options - Configuration options
 * @param {Array} options.assets - Array of asset objects
 * @param {String} options.remark - Optional remark to include in the PDF
 * @param {String} options.title - Title for the PDF
 */
export const generateAssetsPDF = async ({
  assets,
  remark = '',
  title = 'Assets Report'
}) => {
  try {
    // Create a new PDF document
    const pdfDoc = await PDFDocument.create();
    
    // Add a new page to the document - using let instead of const since page will be reassigned
    let page = pdfDoc.addPage([595.28, 841.89]); // A4 size
    
    // Get the standard font
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
    
    // Set up page dimensions
    const { width, height } = page.getSize();
    const margin = 50;
    let yOffset = height - margin;
    
    // Add title
    page.drawText(title, {
      x: margin,
      y: yOffset,
      size: 18,
      font: boldFont,
      color: rgb(0, 0, 0),
    });
    
    yOffset -= 40;
    
    // Add date
    page.drawText(`Generated on: ${new Date().toLocaleString()}`, {
      x: margin,
      y: yOffset,
      size: 10,
      font,
      color: rgb(0, 0, 0),
    });
    
    yOffset -= 30;
    
    // Add remark if provided
    if (remark) {
      page.drawText('Remark: ' + remark, {
        x: margin,
        y: yOffset,
        size: 10,
        font,
        color: rgb(0, 0, 0),
      });
      
      yOffset -= 30;
    }
    
    // Add summary
    const summary = generateSummary(assets);
    let summaryText = 'Summary: ';
    summary.forEach(item => {
      summaryText += `${item.name}: ${item.count}, `;
    });
    
    // Remove trailing comma
    summaryText = summaryText.slice(0, -2);
    
    page.drawText(summaryText, {
      x: margin,
      y: yOffset,
      size: 12,
      font: boldFont,
      color: rgb(0, 0, 0),
    });
    
    yOffset -= 40;
    
    // Draw table headers
    const headerY = yOffset;
    const columnWidths = [40, 120, 120, 100, 160];
    const columnX = [
      margin,
      margin + columnWidths[0],
      margin + columnWidths[0] + columnWidths[1],
      margin + columnWidths[0] + columnWidths[1] + columnWidths[2],
      margin + columnWidths[0] + columnWidths[1] + columnWidths[2] + columnWidths[3]
    ];
    
    // Draw header background
    page.drawRectangle({
      x: margin,
      y: headerY - 20,
      width: width - margin * 2,
      height: 25,
      color: rgb(0.1, 0.4, 0.7),
    });
    
    page.drawText('Sl.No.', {
      x: columnX[0] + 5,
      y: headerY,
      size: 10,
      font: boldFont,
      color: rgb(1, 1, 1),
    });
    
    page.drawText('OEM Tag', {
      x: columnX[1] + 5,
      y: headerY,
      size: 10,
      font: boldFont,
      color: rgb(1, 1, 1),
    });
    
    page.drawText('ORG Tag', {
      x: columnX[2] + 5,
      y: headerY,
      size: 10,
      font: boldFont,
      color: rgb(1, 1, 1),
    });
    
    page.drawText('Name', {
      x: columnX[3] + 5,
      y: headerY,
      size: 10,
      font: boldFont,
      color: rgb(1, 1, 1),
    });
    
    page.drawText('Specifications', {
      x: columnX[4] + 5,
      y: headerY,
      size: 10,
      font: boldFont,
      color: rgb(1, 1, 1),
    });
    
    yOffset = headerY - 30;
    
    // Draw rows
    for (let i = 0; i < assets.length; i++) {
      // Check if we need a new page
      if (yOffset < margin + 50) {
        page = pdfDoc.addPage([595.28, 841.89]);
        yOffset = height - margin;
        
        // Redraw header on new page
        const newHeaderY = yOffset - 20;
        
        page.drawText('Continued...', {
          x: margin,
          y: yOffset,
          size: 14,
          font: boldFont,
          color: rgb(0, 0, 0),
        });
        
        yOffset = newHeaderY;
        
        // Draw header background
        page.drawRectangle({
          x: margin,
          y: yOffset - 20,
          width: width - margin * 2,
          height: 25,
          color: rgb(0.1, 0.4, 0.7),
        });
        
        page.drawText('Sl.No.', {
          x: columnX[0] + 5,
          y: yOffset,
          size: 10,
          font: boldFont,
          color: rgb(1, 1, 1),
        });
        
        page.drawText('OEM Tag', {
          x: columnX[1] + 5,
          y: yOffset,
          size: 10,
          font: boldFont,
          color: rgb(1, 1, 1),
        });
        
        page.drawText('ORG Tag', {
          x: columnX[2] + 5,
          y: yOffset,
          size: 10,
          font: boldFont,
          color: rgb(1, 1, 1),
        });
        
        page.drawText('Name', {
          x: columnX[3] + 5,
          y: yOffset,
          size: 10,
          font: boldFont,
          color: rgb(1, 1, 1),
        });
        
        page.drawText('Specifications', {
          x: columnX[4] + 5,
          y: yOffset,
          size: 10,
          font: boldFont,
          color: rgb(1, 1, 1),
        });
        
        yOffset -= 30;
      }
      
      const asset = assets[i];
      
      // Draw row background (alternating colors)
      page.drawRectangle({
        x: margin,
        y: yOffset - 15,
        width: width - margin * 2,
        height: 25,
        color: i % 2 === 0 ? rgb(0.95, 0.95, 0.95) : rgb(0.9, 0.9, 0.9),
      });
      
      // Draw serial number
      page.drawText((i + 1).toString(), {
        x: columnX[0] + 5,
        y: yOffset,
        size: 9,
        font,
        color: rgb(0, 0, 0),
      });
      
      // Draw OEM Tag
      page.drawText(asset.oem_tag || '-', {
        x: columnX[1] + 5,
        y: yOffset,
        size: 9,
        font,
        color: rgb(0, 0, 0),
      });
      
      // Draw ORG Tag
      page.drawText(asset.org_tag || '-', {
        x: columnX[2] + 5,
        y: yOffset,
        size: 9,
        font,
        color: rgb(0, 0, 0),
      });
      
      // Draw Name
      page.drawText(asset.name || '-', {
        x: columnX[3] + 5,
        y: yOffset,
        size: 9,
        font,
        color: rgb(0, 0, 0),
      });
      
      // Draw Specifications (truncated if needed)
      const specs = formatSpecifications(asset.specification);
      const truncatedSpecs = specs.length > 60 ? specs.substring(0, 60) + '...' : specs;
      
      page.drawText(truncatedSpecs, {
        x: columnX[4] + 5,
        y: yOffset,
        size: 9,
        font,
        color: rgb(0, 0, 0),
      });
      
      yOffset -= 30;
    }
    
    // Convert the PDF to bytes and then to a Blob
    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    
    // Open the PDF in a new tab
    window.open(url, '_blank');
    
    // Clean up the URL after a delay to ensure the PDF opens
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 5000);
    
  } catch (error) {
    console.error("Error generating PDF:", error);
    alert("Failed to generate PDF. Error: " + error.message);
  }
}; 